import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '@/types';
import { identifyUserInClarity, clearUserFromClarity } from '@services/clarity';
import { supabase } from '@lib/supabase';
import useOnboardingStore from './onboardingStore';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  emailSent: boolean;
  sentEmail: string | null;
  lastEmailSentAt: number | null;
  login: (email: string) => Promise<void>;
  resendMagicLink: () => Promise<void>;
  logout: () => Promise<void>;
  checkSession: (skipLoading?: boolean) => Promise<void>;
  clearEmailSentState: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      error: null,
      isAuthenticated: false,
      emailSent: false,
      sentEmail: null,
      lastEmailSentAt: null,

      login: async (email: string) => {
        try {
          set({ isLoading: true, error: null, emailSent: false });

          // Send magic link via Supabase
          const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
              shouldCreateUser: true,
              data: {
                email, // Store email in user metadata when they sign up
              },
              emailRedirectTo: `${window.location.origin}/dashboard`,
            },
          });

          if (error) {
            throw new Error(error.message);
          }

          // Mark email as sent
          set({
            emailSent: true,
            sentEmail: email,
            lastEmailSentAt: Date.now(),
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            emailSent: false,
            error:
              error instanceof Error
                ? error.message
                : 'An error occurred during login',
          });
        }
      },

      resendMagicLink: async () => {
        const { sentEmail } = get();
        if (!sentEmail) {
          set({ error: 'No email address found to resend to' });
          return;
        }

        try {
          set({ isLoading: true, error: null });

          const { error } = await supabase.auth.signInWithOtp({
            email: sentEmail,
            options: {
              shouldCreateUser: true,
              data: {
                email: sentEmail,
              },
              emailRedirectTo: `${window.location.origin}/dashboard`,
            },
          });

          if (error) {
            throw new Error(error.message);
          }

          set({
            lastEmailSentAt: Date.now(),
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'An error occurred while resending the magic link',
          });
        }
      },

      clearEmailSentState: () => {
        set({
          emailSent: false,
          sentEmail: null,
          lastEmailSentAt: null,
          error: null,
        });
      },

      logout: async () => {
        try {
          set({ isLoading: true, error: null });

          // Sign out from Supabase
          await supabase.auth.signOut();

          // Clear dashboard data for the current user
          const useDashboardStore = (await import('./dashboardStore')).default;
          useDashboardStore.getState().clearUserData();

          // Clear user from Clarity tracking
          clearUserFromClarity();

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            emailSent: false,
            sentEmail: null,
            lastEmailSentAt: null,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'An error occurred during logout',
          });
        }
      },

      checkSession: async (skipLoading = false) => {
        const currentState = get();

        // Only show loading if we don't have a user yet or if explicitly requested
        if (!skipLoading && !currentState.user && !currentState.isLoading) {
          set({ isLoading: true });
        }

        try {
          // Check for existing Supabase session
          const {
            data: { session },
            error,
          } = await supabase.auth.getSession();

          if (error) {
            console.warn('Error getting session:', error);
          }

          if (session?.user) {
            // User has an existing session
            const user: User = {
              id: session.user.id,
              email: session.user.email || '',
              firstName:
                session.user.user_metadata?.firstName ||
                (session.user.email
                  ? session.user.email.split('@')[0].charAt(0).toUpperCase() +
                    session.user.email.split('@')[0].slice(1).toLowerCase()
                  : ''),
              lastName: session.user.user_metadata?.lastName,
              phoneNumber: session.user.user_metadata?.phoneNumber,
              onboardingCompleted:
                session.user.user_metadata?.onboardingCompleted || false,
              createdAt: session.user.created_at || new Date().toISOString(),
            };

            // Check if user data has actually changed to avoid unnecessary updates
            const currentUser = currentState.user;
            const userChanged =
              !currentUser ||
              currentUser.id !== user.id ||
              currentUser.email !== user.email ||
              currentUser.firstName !== user.firstName ||
              currentUser.lastName !== user.lastName ||
              currentUser.phoneNumber !== user.phoneNumber ||
              currentUser.onboardingCompleted !== user.onboardingCompleted;

            if (userChanged || !currentState.isAuthenticated) {
              // Re-identify user in Clarity for session continuity
              identifyUserInClarity(user);

              set({
                user,
                isAuthenticated: !!session.user.email, // Authenticated if they have email
                isLoading: false,
                error: null,
              });

              // Hydrate onboarding data from database for all users (authenticated and anonymous)
              useOnboardingStore
                .getState()
                .hydrateFromDatabase()
                .catch(error => {
                  console.warn('Failed to hydrate onboarding data:', error);
                });
            } else {
              // Just update loading state if user data hasn't changed
              set({ isLoading: false });
            }
          } else {
            // No session exists, check if we need to sign in anonymously
            const needsAnonymousSignIn =
              !currentState.user || currentState.isAuthenticated;

            if (needsAnonymousSignIn) {
              const {
                data: { user: anonUser },
                error: anonError,
              } = await supabase.auth.signInAnonymously();

              if (anonError) {
                console.warn('Error signing in anonymously:', anonError);
                set({
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: null,
                });
                return;
              }

              if (anonUser) {
                const user: User = {
                  id: anonUser.id,
                  email: '', // Empty for anonymous users
                  firstName: '', // Empty for anonymous users
                  onboardingCompleted: false,
                  createdAt: anonUser.created_at || new Date().toISOString(),
                };

                // Don't identify anonymous users in Clarity
                set({
                  user,
                  isAuthenticated: false, // Anonymous users are not considered authenticated
                  isLoading: false,
                  error: null,
                });

                // Hydrate onboarding data for anonymous users
                // Wait for the session to be established before making API calls

                // Wait for the session to be fully established
                const waitForSession = async () => {
                  try {
                    // Check if session is available
                    const {
                      data: { session },
                    } = await supabase.auth.getSession();
                    if (session) {
                      await useOnboardingStore.getState().hydrateFromDatabase();
                    }
                  } catch {
                    // Failed to hydrate onboarding data for anonymous user
                  }
                };

                // Wait a moment then check session
                setTimeout(waitForSession, 100);
              } else {
                set({
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: null,
                });
              }
            } else {
              // Just update loading state if we already have an anonymous user
              set({ isLoading: false });
            }
          }
        } catch (error) {
          console.warn('Error in checkSession:', error);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'An error occurred checking the session',
          });
        }
      },
    }),
    {
      name: 'fieldvest-auth',
      partialize: state => ({
        // Persist user and auth state but not loading/error states
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        emailSent: state.emailSent,
        sentEmail: state.sentEmail,
        lastEmailSentAt: state.lastEmailSentAt,
      }),
    }
  )
);
